import React, { useEffect, useState, useRef } from 'react';
import { evidenceAPI, assessmentsAPI } from '../services/api';
import { Assessment, Evidence } from '../types';
import { useAuth } from '../context/AuthContext';

const ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx'];
const MAX_MB = 10;

const EvidencePage: React.FC = () => {
  const { user } = useAuth();
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [selectedAssessment, setSelectedAssessment] = useState<Assessment | null>(null);
  const [evidence, setEvidence] = useState<Evidence[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    assessmentsAPI.list().then((res) => {
      setAssessments(res.data);
      if (res.data.length > 0) loadEvidence(res.data[0]);
    });
  }, []);

  const loadEvidence = async (a: Assessment) => {
    setSelectedAssessment(a);
    const res = await evidenceAPI.list(a.id);
    setEvidence(res.data);
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedAssessment || !e.target.files?.[0]) return;
    const file = e.target.files[0];
    const ext = file.name.split('.').pop()?.toLowerCase() || '';

    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setError(`File type .${ext} not allowed. Permitted: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setError(`File too large. Max: ${MAX_MB} MB`);
      return;
    }

    setError(null);
    setUploading(true);

    const form = new FormData();
    form.append('assessment_id', String(selectedAssessment.id));
    form.append('file', file);

    try {
      await evidenceAPI.upload(form);
      setSuccess('File uploaded successfully');
      await loadEvidence(selectedAssessment);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleVerify = async (id: number) => {
    await evidenceAPI.verify(id);
    if (selectedAssessment) await loadEvidence(selectedAssessment);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this evidence file?')) return;
    await evidenceAPI.delete(id);
    if (selectedAssessment) await loadEvidence(selectedAssessment);
  };

  const canVerify = user?.role === 'super_admin' || user?.role === 'org_admin';

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Evidence Manager</h1>

      {/* Assessment selector */}
      {assessments.length > 0 && (
        <div className="overflow-x-auto pb-1">
          <div className="flex gap-2 flex-nowrap min-w-max">
          {assessments.map((a) => (
            <button
              key={a.id}
              onClick={() => loadEvidence(a)}
              className={`px-3 py-1.5 text-sm rounded-lg border ${
                selectedAssessment?.id === a.id
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'border-gray-200 text-gray-600 hover:border-blue-400'
              }`}
            >
              Assessment #{a.id}
              <span className={`ml-1 text-xs px-1.5 py-0.5 rounded ${
                a.status === 'draft' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
              }`}>{a.status}</span>
            </button>
          ))}
          </div>
        </div>
      )}

      {/* Upload zone */}
      {selectedAssessment && (
        <div className="bg-white border-2 border-dashed border-gray-200 rounded-xl p-8 text-center">
          <span className="text-4xl">📁</span>
          <p className="text-sm text-gray-600 mt-2">Upload evidence for Assessment #{selectedAssessment.id}</p>
          <p className="text-xs text-gray-400 mt-1">Allowed: PDF, JPG, PNG, DOC, DOCX · Max {MAX_MB}MB</p>
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          {success && <p className="mt-2 text-sm text-green-600">{success}</p>}
          <label className="mt-4 inline-block cursor-pointer px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
            {uploading ? 'Uploading…' : 'Choose File'}
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              onChange={handleUpload}
              disabled={uploading}
            />
          </label>
        </div>
      )}

      {/* Evidence list */}
      {evidence.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">File Name</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Size</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Uploaded</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {evidence.map((ev) => (
                <tr key={ev.id} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-800">{ev.file_name}</td>
                  <td className="px-4 py-3 text-gray-500 uppercase text-xs">{ev.file_type}</td>
                  <td className="px-4 py-3 text-gray-500">{ev.file_size ? `${(ev.file_size / 1024).toFixed(0)} KB` : '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{new Date(ev.uploaded_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                      ev.is_verified ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {ev.is_verified ? '✓ Verified' : 'Pending'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      {canVerify && !ev.is_verified && (
                        <button
                          onClick={() => handleVerify(ev.id)}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          Verify
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(ev.id)}
                        className="text-xs text-red-500 hover:underline"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {evidence.length === 0 && selectedAssessment && (
        <p className="text-center text-gray-400 py-8">No evidence uploaded yet for this assessment.</p>
      )}
    </div>
  );
};

export default EvidencePage;
